import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "tbc_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

// Detect once
const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
const isInStandaloneMode =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.debug("[install-pwa] localStorage read failed", e);
    return false;
  }
}

export const InstallPWA = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode) return;
    if (recentlyDismissed()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS doesn't fire beforeinstallprompt — show our custom banner after a small delay
    let iosTimer;
    if (isIOS) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      // ignore localStorage failures (private mode)
      if (process.env.NODE_ENV !== "production") console.debug("[install-pwa] localStorage write failed", e);
    }
    setVisible(false);
    setShowIOSSheet(false);
  };

  const handleInstall = async () => {
    if (isIOS && !deferred) {
      setShowIOSSheet(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  };

  if (!visible || isInStandaloneMode) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 z-[9998] sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px]"
      >
        <div className="rounded-2xl border border-tbc-gold/40 bg-tbc-navy text-white shadow-2xl shadow-black/40 overflow-hidden">
          <div className="flex items-start gap-3 p-4">
            <img
              src="/icon-192.png?v=3"
              alt="The Blk Community"
              className="w-12 h-12 rounded-xl flex-shrink-0 ring-1 ring-tbc-gold/30 object-contain bg-tbc-navy"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold leading-tight">Install The Blk Community</p>
              <p className="text-sm text-white/70 mt-1 leading-snug">
                Add to your home screen for a faster, app-like experience.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid="pwa-install-button"
                  onClick={handleInstall}
                  className="inline-flex items-center gap-2 rounded-lg bg-tbc-gold text-tbc-navy px-3 py-2 text-sm font-semibold hover:bg-tbc-gold/90 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {isIOS ? "How to install" : "Install"}
                </button>
                <button
                  data-testid="pwa-install-dismiss"
                  onClick={dismiss}
                  className="text-sm text-white/60 hover:text-white px-2 py-2"
                >
                  Not now
                </button>
              </div>
            </div>
            <button
              aria-label="Close"
              data-testid="pwa-install-close"
              onClick={dismiss}
              className="text-white/50 hover:text-white p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showIOSSheet && (
        <div
          data-testid="pwa-ios-instructions"
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowIOSSheet(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-tbc-navy text-white border border-tbc-gold/40 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Install on iPhone</h3>
              <button
                onClick={() => setShowIOSSheet(false)}
                className="text-white/60 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-white/85">
              <li className="flex items-start gap-3">
                <span className="flex-none w-6 h-6 rounded-full bg-tbc-gold text-tbc-navy text-xs font-bold flex items-center justify-center">1</span>
                <span>
                  Tap the <Share className="inline w-4 h-4 mx-1 -mt-0.5" /> Share button at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-none w-6 h-6 rounded-full bg-tbc-gold text-tbc-navy text-xs font-bold flex items-center justify-center">2</span>
                <span>Scroll down and tap <strong className="text-tbc-gold">Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-none w-6 h-6 rounded-full bg-tbc-gold text-tbc-navy text-xs font-bold flex items-center justify-center">3</span>
                <span>Tap <strong className="text-tbc-gold">Add</strong> in the top-right corner. Done!</span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-5 w-full rounded-lg bg-tbc-gold text-tbc-navy py-2.5 font-semibold hover:bg-tbc-gold/90"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
